import type {
  MapCameraChangedEvent,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';

import {
  AdvancedMarker,
  APIProvider,
  Map,
  useApiIsLoaded,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getEnv } from '../../lib/env';
import { EMPTY_FUNCTION } from '../../lib/globals';

import Loader from '../Loader/Loader';

import './autocomplete.css';

const libraries = ['marker', 'places'];

const mapsDefaultPropCenter = {
  lat: 0,
  lng: 0,
};

const mapsDefaultPropMarkerPosition = {
  lat: 0,
  lng: 0,
};

type MapPosition = {
  lat: number;
  lng: number;
};

export type MarkerPositionChangeFn = (position: {
  lat: number;
  lon: number;
}) => void;

interface MapsProps {
  /**
   * Position where map should be centered
   */
  center?: MapPosition;
  /**
   * Marker position
   */
  markerPosition?: MapPosition;
  /**
   * If marker changes are disabled
   */
  disabled?: boolean;
  /**
   * On marker position change callback
   */
  onMarkerPositionChange?: MarkerPositionChangeFn;
}

const Maps = (props: MapsProps) => (
  <APIProvider
    apiKey={getEnv('VITE_GOOGLE_API_KEY') || ''}
    libraries={libraries}
  >
    <MapsContent {...props} />
  </APIProvider>
);

const MapsContent = ({
  center = mapsDefaultPropCenter,
  markerPosition = mapsDefaultPropMarkerPosition,
  disabled = false,
  onMarkerPositionChange = EMPTY_FUNCTION,
}: MapsProps) => {
  const { t } = useTranslation();
  const [currentCenter, setCurrentCenter] = useState(center);
  const [currentMarkerPosition, setCurrentMarkerPosition] =
    useState(markerPosition);

  const autocompleteContainerRef = useRef<HTMLDivElement>(null);
  const isLoaded = useApiIsLoaded();
  const placesLibrary = useMapsLibrary('places');

  useEffect(() => {
    setCurrentMarkerPosition(markerPosition);
  }, [markerPosition]);

  useEffect(() => {
    setCurrentCenter(center);
  }, [center]);

  const updateMarkerPosition = useCallback(
    (position: google.maps.LatLngLiteral) => {
      if (disabled) return;
      setCurrentMarkerPosition(position);
      onMarkerPositionChange({ lat: position.lat, lon: position.lng });
    },
    [onMarkerPositionChange, disabled],
  );

  const handleMapClick = useCallback(
    (event: MapMouseEvent) => {
      if (event.detail.latLng) updateMarkerPosition(event.detail.latLng);
    },
    [updateMarkerPosition],
  );

  const handleCenterChanged = useCallback((event: MapCameraChangedEvent) => {
    setCurrentCenter(event.detail.center);
  }, []);

  const handleMarkerDragEnd = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        updateMarkerPosition({
          lat: event.latLng.lat(),
          lng: event.latLng.lng(),
        });
      }
    },
    [updateMarkerPosition],
  );

  const onPlaceSelected = useCallback(
    async (event: Event) => {
      const place = (
        event as google.maps.places.PlacePredictionSelectEvent
      ).placePrediction?.toPlace();

      if (!place) return;

      await place.fetchFields({ fields: ['location'] });
      const newPosition = {
        lat: place.location?.lat?.() || 0,
        lng: place.location?.lng?.() || 0,
      };
      setCurrentMarkerPosition(newPosition);
      setCurrentCenter(newPosition);
      onMarkerPositionChange({ lat: newPosition.lat, lon: newPosition.lng });
    },
    [onMarkerPositionChange],
  );

  useEffect(() => {
    const container = autocompleteContainerRef.current;
    const PlaceAutocompleteElement = placesLibrary?.PlaceAutocompleteElement;
    if (!container || !PlaceAutocompleteElement) return;

    let autocompleteElement: google.maps.places.PlaceAutocompleteElement;
    try {
      autocompleteElement = new PlaceAutocompleteElement({});
    } catch (error) {
      console.error(
        'Google Maps Places Autocomplete failed to initialize. Check the API key referrer restrictions.',
        error,
      );
      return;
    }

    autocompleteElement.setAttribute('placeholder', t('ContentForm.FindPlace'));
    autocompleteElement.className = 'maps-autocomplete';
    autocompleteElement.setAttribute('aria-disabled', String(disabled));
    autocompleteElement.addEventListener('gmp-select', onPlaceSelected);
    container.replaceChildren(autocompleteElement);

    return () => {
      autocompleteElement.removeEventListener('gmp-select', onPlaceSelected);
      autocompleteElement.remove();
    };
  }, [disabled, onPlaceSelected, placesLibrary, t]);

  return (
    <div className="w-full h-fit border border-white-600 dark:border-slate-800 rounded-lg overflow-hidden">
      {isLoaded ? (
        <>
          <div ref={autocompleteContainerRef} />
          <Map
            className={twMerge(
              'w-full h-[280px]',
              disabled && 'pointer-events-none',
            )}
            mapId={getEnv('VITE_GOOGLE_MAP_ID') || 'DEMO_MAP_ID'}
            center={currentCenter}
            defaultZoom={10}
            onClick={handleMapClick}
            onCenterChanged={handleCenterChanged}
          >
            <AdvancedMarker
              position={currentMarkerPosition}
              draggable={!disabled}
              onDragEnd={handleMarkerDragEnd}
            />
          </Map>
        </>
      ) : (
        <div className="border-slate-200 border rounded-lg flex items-center justify-center w-full h-[280px]">
          <Loader size="small" type="spinner-grid" />
        </div>
      )}
    </div>
  );
};

export default Maps;
